"use client";

import { useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { SearchIcon, LocateFixedIcon } from "lucide-react";

import { EventCard, type PublicEvent } from "@/components/events/EventCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PillSelect } from "@/components/ui/pill-select";
import { DateRangeFilter, type DateRange } from "@/components/events/DateRangeFilter";
import { cn } from "@/lib/utils";
import { EVENT_CATEGORIES, EVENT_TYPES, type EventCategoryKey, type EventTypeKey } from "@/lib/constants";
import { useGeolocation, distanceKm } from "@/lib/geo";
import { toDisplayDate } from "@/lib/eventDate";

type SortMode = "chrono" | "near";

export function EventsExplorer({
  events,
  organizations,
}: {
  events: PublicEvent[];
  organizations: { id: number; name: string }[];
}) {
  const searchParams = useSearchParams();

  const [sort, setSort] = useState<SortMode>("chrono");
  const [eventType, setEventType] = useState<EventTypeKey | "ALL">("ALL");
  const [category, setCategory] = useState<EventCategoryKey | "ALL">("ALL");
  const [organizationId, setOrganizationId] = useState<string>(
    () => searchParams.get("organizador") ?? "ALL"
  );
  const [dateRange, setDateRange] = useState<DateRange>({ from: null, to: null });
  const [freeOnly, setFreeOnly] = useState(false);
  const [query, setQuery] = useState("");

  const geo = useGeolocation();

  function toggleNearSort() {
    if (sort === "near") {
      setSort("chrono");
      return;
    }
    if (geo.status === "idle") geo.request();
    setSort("near");
  }

  const filtered = useMemo(() => {
    const from = dateRange.from ? new Date(`${dateRange.from}T00:00:00`) : null;
    const to = dateRange.to ? new Date(`${dateRange.to}T23:59:59`) : null;

    return events.filter((e) => {
      if (eventType !== "ALL" && e.eventType !== eventType) return false;
      if (category !== "ALL" && e.category !== category) return false;
      if (organizationId !== "ALL" && String(e.organizationId) !== organizationId) return false;
      if (freeOnly && !(e.price && /gratu|libre/i.test(e.price))) return false;
      const eventDate = toDisplayDate(new Date(e.startsAt));
      if (from && eventDate < from) return false;
      if (to && eventDate > to) return false;
      if (query && !`${e.title} ${e.organizationName}`.toLowerCase().includes(query.toLowerCase()))
        return false;
      return true;
    });
  }, [events, eventType, category, organizationId, dateRange, freeOnly, query]);

  const withDistance = useMemo(() => {
    if (sort !== "near" || !geo.position) return filtered.map((e) => ({ event: e, distance: null as number | null }));
    return filtered.map((e) => ({
      event: e,
      distance:
        e.latitude != null && e.longitude != null
          ? distanceKm(geo.position!, { latitude: e.latitude, longitude: e.longitude })
          : null,
    }));
  }, [filtered, sort, geo.position]);

  const sorted = useMemo(() => {
    const list = [...withDistance];
    if (sort === "near" && geo.position) {
      list.sort((a, b) => {
        if (a.distance == null) return 1;
        if (b.distance == null) return -1;
        return a.distance - b.distance;
      });
    }
    return list;
  }, [withDistance, sort, geo.position]);

  const sortLabel =
    sort === "near" && geo.status === "granted"
      ? "cercanía"
      : sort === "near" && geo.status === "loading"
        ? "cercanía (localizando…)"
        : "fecha";

  return (
    <div id="eventos" className="scroll-mt-20">
      <div className="flex flex-col gap-4">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar eventos, autores, librerías…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-11 rounded-full pl-10 text-base"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={sort === "near" ? "default" : "outline"}
            className="rounded-full"
            onClick={toggleNearSort}
          >
            <LocateFixedIcon className="size-3.5" />
            Cerca de ti
          </Button>

          <PillSelect
            active={eventType !== "ALL"}
            value={eventType}
            onChange={(e) => setEventType(e.target.value as EventTypeKey | "ALL")}
          >
            <option value="ALL">Tipo de evento</option>
            {(Object.keys(EVENT_TYPES) as EventTypeKey[]).map((key) => (
              <option key={key} value={key}>
                {EVENT_TYPES[key]}
              </option>
            ))}
          </PillSelect>

          <PillSelect
            active={category !== "ALL"}
            value={category}
            onChange={(e) => setCategory(e.target.value as EventCategoryKey | "ALL")}
          >
            <option value="ALL">Categoría</option>
            {(Object.keys(EVENT_CATEGORIES) as EventCategoryKey[]).map((key) => (
              <option key={key} value={key}>
                {EVENT_CATEGORIES[key].label}
              </option>
            ))}
          </PillSelect>

          <PillSelect
            active={organizationId !== "ALL"}
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
          >
            <option value="ALL">Organizador</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </PillSelect>

          <DateRangeFilter value={dateRange} onChange={setDateRange} />

          <Button
            size="sm"
            variant={freeOnly ? "default" : "outline"}
            className="rounded-full"
            onClick={() => setFreeOnly((v) => !v)}
          >
            Entrada libre
          </Button>
        </div>

        <p className="text-xs font-semibold tracking-wide text-primary uppercase">
          {sorted.length} evento{sorted.length === 1 ? "" : "s"} · ordenado por {sortLabel}
        </p>
      </div>

      {geo.status === "denied" && sort === "near" && (
        <p className="mt-4 text-sm text-muted-foreground">
          No hemos podido acceder a tu ubicación. Activa la localización en el navegador o usa el
          orden cronológico.
        </p>
      )}

      {sorted.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          No hay eventos que coincidan con tu búsqueda.
        </div>
      ) : (
        <div className={cn("mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3")}>
          {sorted.map(({ event, distance }) => (
            <EventCard key={event.id} event={event} distanceKm={distance} />
          ))}
        </div>
      )}
    </div>
  );
}
